import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import CourseForm, { type CourseRecord } from '@/pages/dashboard/courses/components/CourseForm';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

function CourseEdit() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user || !id) return;
      setLoading(true);
      const { data, error: err } = await supabase
        .from('courses')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!mounted) return;
      if (err) {
        setError(err.message);
      } else if (data) {
        setCourse({
          id: data.id,
          owner_id: data.owner_id,
          title: data.title,
          instructor: data.instructor || '',
          category: data.category || '',
          level: data.level || '',
          price: data.price !== null ? Number(data.price) : null,
          duration_hours: data.duration_hours !== null ? Number(data.duration_hours) : null,
          lessons_count: data.lessons_count !== null ? Number(data.lessons_count) : null,
          language: data.language || 'Français',
          description: data.description || '',
          objectives: data.objectives || '',
        });
      } else {
        setError('Formation introuvable.');
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user, id]);

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Modifier la formation</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Mettez à jour les informations de votre formation.
        </p>
      </div>

      <div className="max-w-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-sm text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/courses')}
              className="px-5 py-2.5 border border-background-300/60 text-foreground-50 font-medium rounded-lg hover:bg-background-200/60 transition-colors text-sm whitespace-nowrap"
            >
              Retour à mes formations
            </button>
          </div>
        ) : course ? (
          <CourseForm ownerId={user.id} existing={course} onSaved={() => navigate('/dashboard/courses')} />
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default CourseEdit;